import { User } from '../types'

/**
 * Granular permissions (Framework Part D). Super admins implicitly hold all of
 * them. These layer on top of the legacy `role` field — a user with role
 * 'admin' is a super admin (set at migration 012).
 */
export interface PermissionDef {
  key: string
  name: string
  description: string
}

export interface PermissionGroup {
  title: string
  permissions: PermissionDef[]
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    title: 'Deal Management',
    permissions: [
      { key: 'perm-create-deal', name: 'Create Deal', description: 'Source vehicles, create deals' },
      { key: 'perm-send-bos', name: 'Send Vehicle Purchase Agreement', description: 'Send BoS links to sellers' },
      { key: 'perm-upload-dd', name: 'Upload DD Documents', description: 'Upload due-diligence docs' },
      { key: 'perm-elect-deposit', name: 'Elect PPSR Lodgement', description: 'Set the PPSR lodgement election on a Vehicle Purchase Agreement' },
    ],
  },
  {
    title: 'Due Diligence',
    permissions: [
      { key: 'perm-review-dd', name: 'Review DD', description: 'Review & approve DD completeness' },
      { key: 'perm-book-inspection', name: 'Book Inspection', description: 'Schedule inspections' },
    ],
  },
  {
    title: 'Payments & Approvals',
    permissions: [
      { key: 'perm-1st-approval', name: '1st Payment Approval', description: 'First payment approval (Winnie)' },
      { key: 'perm-2nd-release', name: '2nd Payment Release', description: 'Final approval & WBC trigger' },
    ],
  },
  {
    title: 'AR & Invoicing',
    permissions: [
      { key: 'perm-issue-ar', name: 'Issue AR Invoices', description: 'Create & issue AR invoices' },
      { key: 'perm-manage-collections', name: 'Manage Collections', description: 'AR queue, reminders, call logging' },
      { key: 'perm-approve-ar-revision', name: 'Approve AR Revision', description: 'Approve invoice amount changes' },
      { key: 'perm-release-pickup', name: 'Release Pickup', description: 'Release pickup address to buyer' },
    ],
  },
  {
    title: 'Administration',
    permissions: [
      { key: 'perm-override-exceptions', name: 'Override Exceptions', description: 'Override deal gates (reason logged)' },
      { key: 'perm-config-rates', name: 'Configure Rates', description: 'Set commission rates per member' },
      { key: 'perm-override-deposit', name: 'Override PPSR Election', description: 'Change a locked PPSR lodgement election' },
      { key: 'perm-demand-letter', name: 'Sign-off Demand Letter', description: 'Approve 30+ day formal demands' },
      { key: 'perm-bas-signoff', name: 'Sign-off BAS', description: 'Approve GST register for BAS' },
      { key: 'perm-user-management', name: 'User Management', description: 'Add/edit/deactivate users' },
      { key: 'perm-config-notifications', name: 'Configure Notifications', description: 'Set email recipients' },
      { key: 'perm-ppsr-lodge', name: 'Lodge/Discharge PPSR', description: 'Lodge and discharge PPSR security interests on the national register' },
    ],
  },
  {
    title: 'Auditing',
    permissions: [
      { key: 'perm-view-audit', name: 'View Audit Trail', description: 'Read-only audit trail access' },
      { key: 'perm-view-reports', name: 'View Reports', description: 'Sales, commission, contracts & month-end reports' },
      { key: 'perm-view-inbox', name: 'View Smart Inbox', description: 'Incoming emails & classification suggestions' },
    ],
  },
]

export const ALL_PERMISSIONS: PermissionDef[] = PERMISSION_GROUPS.flatMap((g) => g.permissions)

/** Does the user hold a permission? Super admins hold everything. */
export function hasPermission(user: User | null | undefined, key: string): boolean {
  if (!user) return false
  if (user.is_super_admin || user.role === 'admin') return true
  return !!user.permissions?.[key]
}

export function isSuperAdmin(user: User | null | undefined): boolean {
  return !!user && (user.is_super_admin === true || user.role === 'admin')
}
